'use client';

import { css, styled } from 'styled-components';
import Link from 'next/link';
import footerData from '@content/navigation/footerData';
import * as Styled from '@lib/styles';
import Section from '@dvd/components/Section';
import WistiaLogo from '@dvd/components/WistiaLogo';
import InstagramIcon from '@dvd/components/shareIcons/InstagramIcon';
import LinkedInIcon from '@dvd/components/shareIcons/LinkedInIcon';
import FacebookIcon from '@dvd/components/shareIcons/FacebookIcon';
import TiktokIcon from '@dvd/components/shareIcons/TiktokIcon';
import XIcon from '@dvd/components/shareIcons/XIcon';
import { trackLink } from '@lib/tracking';

type FooterProps = {
  /**
   * Theme color option
   *  - light
   *  - dark
   *  - blueDark, for pages that run blue800 end to end
   */
  theme?: 'dark' | 'light' | 'blueDark';
};

const SOCIAL_LINKS = [
  {
    label: 'X',
    href: 'https://twitter.com/wistia',
    icon: <XIcon />,
  },
  {
    label: 'TikTok',
    href: 'https://www.tiktok.com/@wistia',
    icon: <TiktokIcon />,
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/wistia',
    icon: <InstagramIcon />,
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/wistia',
    icon: <LinkedInIcon />,
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/wistia',
    icon: <FacebookIcon />,
  },
];

const FooterNavListItem = styled.li`
  list-style: none;

  & p {
    margin: 0;
  }

  a[class] {
    font-weight: ${({ theme }) => theme.typography.fontWeight.body};
    text-decoration: underline;
    text-decoration-color: transparent;
    transition: text-decoration-color var(--global-motion);
  }

  a,
  a:active,
  a:visited,
  a:hover {
    color: inherit;
  }

  a:hover {
    text-decoration-color: currentcolor;
  }
`;

const FooterContainer = styled(Section)`
  padding-block: ${({ theme }) => theme.spacing[2]};

  ${({ theme }) => theme.mq.minWidth.large} {
    padding-block: ${({ theme }) => theme.spacing[4]};
  }
`;

const FooterSections = styled.div`
  /**
   * Below the large breakpoint, flow the category groups as CSS multi-column
   * content so they balance into even columns (two at small, three at medium)
   * top-to-bottom, rather than staying bundled into the uneven desktop columns.
   * At large we switch to the desktop grid.
   */
  column-count: 2;
  column-gap: ${({ theme }) => theme.spacing[1]};
  /** 2rem gap between the end of the link lists and the bottom row (SOC 2, copyright, social). */
  margin-block-end: ${({ theme }) => theme.spacing[2]};

  ${({ theme }) => theme.mq.minWidth.medium} {
    column-count: 3;
  }
  ${({ theme }) => theme.mq.minWidth.large} {
    column-count: unset;
    display: grid;
    ${({ theme }) => theme.grid.columns.large};
  }
`;

const FooterNavList = styled.ul`
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  /** DVD spacing-1 (4px / theme.spacing[0.25]) — smallest token — between every column item. */
  row-gap: ${({ theme }) => theme.spacing[0.25]};
`;

/**
 * Shared bold-label treatment for the footer’s small headings and the SOC 2
 * line: the eyebrow emphasis tokens (weight, line-height) on the BodyXs scale.
 * DVD has no headingSix tokens, so this deliberately borrows the eyebrow
 * pairing rather than mixing one-off values.
 */
const footerLabelStyles = css`
  font-weight: ${({ theme }) => theme.typography.fontWeight.eyebrow};
  line-height: ${({ theme }) => theme.typography.lineHeight.eyebrow};
`;

const FooterHeading = styled(Styled.BodyXs)`
  ${footerLabelStyles}
  /** Match the list-item row-gap so heading sits as tight as the links. */
  margin: 0 0 ${({ theme }) => theme.spacing[0.25]};
`;

const FooterNavGroup = styled.div`
  /**
   * Keep each category group intact within a multi-column flow (never split a
   * heading from its links across columns). Space above each heading is
   * applied with padding-top rather than margin: browsers drop margins at
   * multi-column fragmentation boundaries, but padding survives, so this
   * reliably separates stacked categories.
   */
  break-inside: avoid;
  /** DVD spacing-6 (24px) between sections. */
  padding-top: ${({ theme }) => theme.spacing[1.5]};

  ${({ theme }) => theme.mq.minWidth.large} {
    padding-top: 0;

    & + & {
      padding-top: ${({ theme }) => theme.spacing[1.5]};
    }
  }
`;

const FooterNavSection = styled.nav`
  /**
   * Below large, dissolve the desktop column wrapper so its heading groups
   * participate directly in the multi-column flow and balance evenly across
   * the narrow columns.
   */
  display: contents;

  ${({ theme }) => theme.mq.minWidth.large} {
    display: flex;
    flex-direction: column;
    grid-column: auto / span 2;
  }
`;

const FooterLogo = styled.div`
  padding-block: ${({ theme }) => theme.spacing[1]};
  text-align: start;
  text-wrap: balance;
  /**
   * Below large, FooterSections is a CSS multi-column flow; span the logo
   * across all columns so it sits above the balanced nav columns rather than
   * flowing as one narrow column item.
   */
  column-span: all;

  ${({ theme }) => theme.mq.minWidth.large} {
    /** First column of the 12-col grid, spanning 2 like each nav column. */
    column-span: none;
    grid-column: auto / span 2;
    padding-block: 0;
  }

  p {
    font-family: var(--font-walsheim);
    /** One type token down from the inherited body (bodyMd) → bodySm. */
    font-size: ${({ theme }) => theme.typography.fontSize.bodySm};
    margin: 0;

    ${({ theme }) => theme.mq.minWidth.large} {
      font-size: ${({ theme }) => theme.typography.fontSize.desktop.bodySm};
    }
  }
`;

const Logo = styled(WistiaLogo)`
  margin-block-end: ${({ theme }) => theme.spacing[1]};

  /** custom per design */
  max-width: 132px;
  width: 100%;
`;

const TrustCenter = styled(Styled.BodyXs)`
  ${footerLabelStyles}
  display: block;
  text-decoration: none;
  text-align: center;
  margin-block-end: ${({ theme }) => theme.spacing[1]};

  ${({ theme }) => theme.mq.minWidth.medium} {
    text-align: start;
    margin-block-end: 0;
  }
`;

const FooterLinks = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;

  p {
    margin: 0;
  }

  a {
    color: currentcolor;
  }

  ${({ theme }) => theme.mq.minWidth.medium} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const ShareLinks = styled.div<{ $theme: string }>`
  color: ${({ theme, $theme }) =>
    $theme === 'light' ? theme.colors.blue500 : 'inherit'};
  display: flex;
  margin-block-start: ${({ theme }) => theme.spacing[2]};
  gap: ${({ theme }) => theme.spacing[1.25]};

  svg {
    height: 1.125rem;
    width: 1.125rem;
  }

  ${({ theme }) => theme.mq.minWidth.medium} {
    margin-block-start: 0;
  }
`;

const ShareLinkTitle = styled.span.attrs({
  className: 'visually-hidden',
})``;

export const CURRENT_YEAR = new Date().getFullYear();

const Footer = ({ theme = 'light' }: FooterProps) => (
  <footer>
    <FooterContainer colorScheme={theme}>
      <FooterSections>
        <FooterLogo>
          <Logo color={theme === 'light' ? 'full' : 'light'} />
          <p>Your complete video marketing platform</p>
        </FooterLogo>
        {footerData.map((column) => (
          <FooterNavSection key={column.name} className={column.name}>
            {column.groups.map((group) => (
              <FooterNavGroup key={group.heading}>
                <FooterHeading as="h6">{group.heading}</FooterHeading>
                <FooterNavList>
                  {group.links.map((link) => (
                    <FooterNavListItem key={link.label}>
                      <Styled.BodyXs
                        as={Link}
                        href={link.href}
                        onClick={() =>
                          trackLink(link.trackingLabel, 'footer-nav')
                        }
                        rel={link.openInNewTab ? 'noopener' : undefined}
                        target={link.openInNewTab ? '_blank' : undefined}
                      >
                        {link.label}
                      </Styled.BodyXs>
                    </FooterNavListItem>
                  ))}
                </FooterNavList>
              </FooterNavGroup>
            ))}
          </FooterNavSection>
        ))}
      </FooterSections>
      <TrustCenter
        as="abbr"
        title="Wistia is certified compliant SOC 2® - SOC for Service Organizations"
      >
        SOC 2 Certified
      </TrustCenter>
      <FooterLinks>
        <Styled.BodyXs>
          ©2001–{CURRENT_YEAR} All rights reserved.{' '}
          <Link
            href="/site-map"
            onClick={() => trackLink('site-map', 'footer-nav')}
          >
            Site Map
          </Link>
          ,{' '}
          <Link
            href="/privacy"
            onClick={() => trackLink('privacy', 'footer-nav')}
          >
            Privacy
          </Link>
          , and{' '}
          <Link
            href="/terms"
            onClick={() => trackLink('terms-of-service', 'footer-nav')}
          >
            Terms
          </Link>
        </Styled.BodyXs>

        <ShareLinks $theme={theme}>
          <p className="visually-hidden">Follow us on social media!</p>
          {SOCIAL_LINKS.map((link) => {
            return (
              <Link
                key={link.label}
                href={link.href}
                title={`Follow us on ${link.label}`}
                onClick={() =>
                  trackLink(`social-${link.label}`, 'footer-social-link')
                }
              >
                {link.icon}
                <ShareLinkTitle>{`Follow us on ${link.label}`}</ShareLinkTitle>
              </Link>
            );
          })}
        </ShareLinks>
      </FooterLinks>
    </FooterContainer>
  </footer>
);

export default Footer;
